'use client';

import React, { useEffect, useRef, useState } from 'react';

interface AnimatedCounterProps {
  end: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
}

function AnimatedCounter({ end, suffix = '', prefix = '', duration = 2000 }: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

const stats = [
  { value: 500, suffix: '+', label: 'Projects Delivered', icon: '📊' },
  { value: 98, suffix: '%', label: 'Client Satisfaction', icon: '⭐' },
  { value: 15, suffix: '+', label: 'Industries Served', icon: '🏢' },
  { value: 50, suffix: '+', label: 'Expert Consultants', icon: '👥' },
];

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    const el = document.getElementById('services');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      {/* Radial gradient overlays */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 20% 50%, rgba(66, 132, 245, 0.18) 0%, rgba(0,0,0,0) 40%), radial-gradient(circle at 80% 50%, rgba(43, 69, 127, 0.3) 0%, rgba(0,0,0,0) 45%), radial-gradient(circle at 50% 100%, rgba(0, 189, 165, 0.1) 0%, rgba(0,0,0,0) 50%)',
        }}
      />

      {/* Dot grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(66, 132, 245, 0.4) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Announcement bar */}
      <div
        className="relative z-10 text-center py-2 px-4 text-xs font-medium"
        style={{
          background:
            'radial-gradient(at 50% 100%, rgba(255, 224, 84, 0.22) 0%, rgba(0,0,0,0) 45%), radial-gradient(circle at 20% 50%, rgba(66, 132, 245, 0.18) 0%, rgba(0,0,0,0) 40%)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <span className="text-gray-400">Delivering </span>
        <span className="text-accent-yellow font-semibold">Strategic Business Transformation</span>
        <span className="text-gray-400"> across Engineering, Accounting, Research &amp; Management</span>
      </div>

      {/* Main hero content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-6 w-full py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text content */}
            <div>
              <div
                className={`transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-6"
                  style={{
                    background: 'rgba(66, 132, 245, 0.15)',
                    border: '1px solid rgba(66, 132, 245, 0.3)',
                    color: '#60A5FA',
                  }}
                >
                  <span className="w-2 h-2 rounded-full bg-accent animate-pulse-slow" />
                  Business Consulting &amp; Technology Solutions
                </div>

                <h1
                  className="text-white font-black mb-6 leading-tight"
                  style={{ fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: '1.15' }}
                >
                  Transforming Business
                  <span
                    className="block"
                    style={{
                      background: 'linear-gradient(135deg, #4284F5, #60A5FA, #00BDA5)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Challenges into
                  </span>
                  Strategic Opportunities
                </h1>
              </div>

              <div
                className={`transition-all duration-700 delay-200 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-xl">
                  Pluto7 Consulting Inc. helps organizations improve business performance through
                  strategic consulting, technology solutions, research, engineering, and management
                  expertise tailored to your unique challenges.
                </p>

                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={scrollToContact}
                    className="px-8 py-4 text-white font-semibold rounded-btn shadow-btn transition-all duration-200 hover:opacity-90 hover:shadow-lg hover:-translate-y-0.5"
                    style={{
                      background: 'linear-gradient(291deg, #4284F5 0%, #21427B 100%)',
                      fontSize: '16px',
                    }}
                  >
                    Request a Consultation
                  </button>
                  <button
                    onClick={scrollToServices}
                    className="px-8 py-4 font-semibold rounded-btn transition-all duration-200 hover:bg-primary/10"
                    style={{
                      border: '2px solid #4284F5',
                      color: '#4284F5',
                      fontSize: '16px',
                      background: 'transparent',
                    }}
                  >
                    Explore Our Services
                  </button>
                </div>
              </div>

              {/* Stats row */}
              <div
                className={`mt-12 transition-all duration-700 delay-300 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div
                  className="grid grid-cols-2 gap-4 p-5 rounded-card"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  {stats.map((stat, i) => (
                    <div key={i} className="text-center">
                      <div
                        className="text-2xl font-black mb-1"
                        style={{
                          background: 'linear-gradient(135deg, #4284F5, #60A5FA)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                      </div>
                      <div className="text-gray-400 text-xs">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Visual dashboard card */}
            <div
              className={`transition-all duration-700 delay-400 ${
                visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
            >
              <HeroDashboard />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <div className="relative z-10 flex justify-center pb-8">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-gray-500 text-xs">Scroll to explore</span>
          <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </section>
  );
}

function HeroDashboard() {
  return (
    <div className="relative">
      {/* Main dashboard card */}
      <div
        className="rounded-card p-6 relative overflow-hidden"
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(10px)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-widest mb-1">CONSULTING OVERVIEW</div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-white font-semibold text-sm">Active Engagements</span>
            </div>
          </div>
          <div
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(0, 189, 165, 0.2)', color: '#00BDA5' }}
          >
            Live
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {[
            { label: 'Strategy Projects', value: '24', change: '+3', positive: true },
            { label: 'Tech Implementations', value: '18', change: '+5', positive: true },
            { label: 'Research Studies', value: '12', change: '+2', positive: true },
            { label: 'Mgmt Consulting', value: '31', change: '+7', positive: true },
          ].map((metric, i) => (
            <div
              key={i}
              className="p-3 rounded-xl"
              style={{
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <div className="text-gray-400 text-xs mb-1">{metric.label}</div>
              <div className="flex items-end gap-2">
                <span className="text-white font-bold text-xl">{metric.value}</span>
                <span className="text-xs font-medium mb-0.5" style={{ color: '#00BDA5' }}>{metric.change}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Activity stream */}
        <div
          className="rounded-xl p-4"
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-400 uppercase tracking-widest">RECENT ACTIVITY</span>
            <span className="text-xs text-primary">View All</span>
          </div>
          {[
            { action: 'Strategic Assessment', client: 'Enterprise Client A', time: '2h ago' },
            { action: 'Tech Roadmap Delivered', client: 'Mid-Market Client B', time: '5h ago' },
            { action: 'Process Optimization', client: 'SMB Client C', time: '1d ago' },
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <div>
                <div className="text-white text-xs font-medium">{item.action}</div>
                <div className="text-gray-500 text-xs">{item.client}</div>
              </div>
              <span className="text-gray-500 text-xs">{item.time}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Floating accent card */}
      <div
        className="absolute -bottom-4 -right-4 p-4 rounded-xl animate-float"
        style={{
          background: 'linear-gradient(135deg, rgba(66, 132, 245, 0.9), rgba(33, 66, 123, 0.9))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <div className="text-white text-xs font-medium mb-1">Client Satisfaction</div>
        <div className="text-white font-black text-2xl">98%</div>
        <div className="text-blue-200 text-xs">↑ +2.4% this quarter</div>
      </div>
    </div>
  );
}
